import React, { useState, useEffect, useCallback } from "react";
import { Calculator, CheckCircle2, XCircle, Plus, X, Pencil, Trash2 } from "lucide-react";
import axios from "axios";
import { ROLE_OPTIONS, ROLE_LABELS } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RoleBranchFields = ({ formData, setFormData, branches }) => (
  <>
    <div className="grid grid-cols-2 gap-5">
      <div>
        <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Role</label>
        <select required data-testid="staff-role-select" value={formData.role} onChange={e=>setFormData({...formData, role: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">{ROLE_OPTIONS.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select>
      </div>
      <div>
        <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Base Salary (₹)</label>
        <input required type="number" min="0" value={formData.base_salary} onChange={e=>setFormData({...formData, base_salary: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
      </div>
    </div>
    {formData.role === "DM" ? (
      <div>
        <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Assigned Branches (District)</label>
        <div className="flex flex-wrap gap-2">
          {branches.map(b => {
            const sel = (formData.assigned_branches || []).includes(b.name);
            return (
              <button type="button" key={b.id} data-testid={`assign-branch-${b.name}`} onClick={() => setFormData({ ...formData, assigned_branches: sel ? formData.assigned_branches.filter(x => x !== b.name) : [...(formData.assigned_branches||[]), b.name] })} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${sel ? "bg-primary text-[#111827] border-primary" : "bg-card_bg text-text_secondary border-border_soft"}`}>{b.name}</button>
            );
          })}
        </div>
      </div>
    ) : (
      <div>
        <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Branch</label>
        <select data-testid="staff-branch-select" value={formData.branch} onChange={e=>setFormData({...formData, branch: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
          {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
      </div>
    )}
  </>
);

const AddEmployeeModal = ({ onClose, onAdd, branches }) => {
  const [formData, setFormData] = useState({ name: "", role: "Receptionist", base_salary: 25000, email: "", password: "", branch: branches[0]?.name || "Main", assigned_branches: [] });
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative overflow-y-auto max-h-[90vh] shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-6">Add Staff</h2>
        <form onSubmit={(e) => { e.preventDefault(); onAdd(formData); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Full Name</label>
            <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <RoleBranchFields formData={formData} setFormData={setFormData} branches={branches} />
          <div className="mt-8 pt-6 border-t border-border_soft/30">
            <h3 className="text-sm font-bold text-gold mb-1">Portal Login Access</h3>
            <p className="text-[11px] text-text_secondary mb-4 leading-relaxed">Staff use these credentials to sign into the SVT Portal. Permissions are strictly locked based on their assigned role.</p>
            <div className="space-y-5">
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Login Email</label>
                <input required type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Temporary Password</label>
                <input required type="text" value={formData.password} onChange={e=>setFormData({...formData, password: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
            </div>
          </div>
          <button type="submit" data-testid="create-staff-btn" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Create Profile</button>
        </form>
      </div>
    </div>
  );
};

const EditEmployeeModal = ({ onClose, onEdit, emp, branches }) => {
  const [formData, setFormData] = useState({ assigned_branches: [], branch: "Main", ...emp });
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative overflow-y-auto max-h-[90vh] shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-6">Edit Employee</h2>
        <form onSubmit={(e) => { e.preventDefault(); onEdit(formData); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Full Name</label>
            <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <RoleBranchFields formData={formData} setFormData={setFormData} branches={branches} />
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Login Email</label>
            <input required type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Changes</button>
        </form>
      </div>
    </div>
  );
};

export default function HR() {
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);

  const fetchHR = useCallback(async () => {
    try {
      const [res, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hr`),
        axios.get(`${BACKEND_URL}/api/branches`),
      ]);
      setEmployees(res.data);
      setBranches(b.data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchHR(); }, [fetchHR]);

  const markAttendance = async (id, status) => {
    try {
      await axios.post(`${BACKEND_URL}/api/hr/${id}/attendance`, { present: status });
      fetchHR();
    } catch(e) { console.error(e); }
  };

  const handleAdd = async (formData) => {
    try {
      await axios.post(`${BACKEND_URL}/api/hr`, formData);
      setShowModal(false);
      fetchHR();
    } catch(e) { alert(e.response?.data?.detail || "Error creating employee"); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/update/hr/${data.id}`, data);
      setShowEdit(null);
      fetchHR();
    } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Remove this employee profile permanently?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/hr/${id}`);
        fetchHR();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Staff</h1>
          <p className="text-text_secondary text-sm">Manage staff profiles, role permissions, and daily attendance.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md">
          <Plus size={20} /> Add Staff
        </button>
      </div>

      <div className="bg-card_bg border border-border_soft rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead className="bg-card_bg/80 backdrop-blur-sm border-b border-border_soft/50">
              <tr>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Staff Member</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Role & Access</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Base Salary</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Attendance</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold text-center">Daily Log</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold text-right">Est. Payout</th>
                <th className="px-8 py-5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border_soft/40">
              {employees.length === 0 && <tr><td colSpan="7" className="px-8 py-8 text-text_secondary text-center">No staff records found.</td></tr>}
              {employees.map((emp) => {
                const calcPay = Math.round(((emp.base_salary||0) / 30) * (emp.attendance_days||0));
                return (
                  <tr key={emp.id} className="hover:bg-card_bg/30 transition-colors group">
                    <td className="px-8 py-5">
                      <p className="text-text_primary font-bold text-sm">{emp.name}</p>
                      <p className="text-xs text-text_secondary mt-1">{emp.email}</p>
                    </td>
                    <td className="px-8 py-5">
                      <span className="bg-primary/10 border border-primary/20 text-gold px-3 py-1.5 rounded-lg text-xs font-semibold">{ROLE_LABELS[emp.role] || emp.role || "—"}</span>
                      <p className="text-[11px] text-text_secondary mt-1.5">{emp.role === "DM" ? (emp.assigned_branches || []).join(", ") || "No branches" : (emp.branch || "—")}</p>
                    </td>
                    <td className="px-8 py-5 text-text_primary text-sm font-mono tracking-wide">₹{(emp.base_salary||0).toLocaleString()}</td>
                    <td className="px-8 py-5 text-text_secondary text-sm"><span className="text-text_primary font-bold">{emp.attendance_days||0}</span> / 30 Days</td>
                    <td className="px-8 py-5">
                      <div className="flex justify-center gap-2">
                        <button onClick={()=>markAttendance(emp.id, true)} className={`p-2.5 rounded-lg transition-all ${emp.present_today ? 'bg-accent_success text-[#111827] shadow-[0_0_15px_rgba(52,211,153,0.4)]' : 'bg-card_bg border border-border_soft text-text_secondary hover:text-text_primary hover:border-text_secondary'}`} title="Mark Present"><CheckCircle2 size={16} strokeWidth={2.5}/></button>
                        <button onClick={()=>markAttendance(emp.id, false)} className={`p-2.5 rounded-lg transition-all ${!emp.present_today ? 'bg-accent_danger text-text_primary shadow-[0_0_15px_rgba(248,113,113,0.4)]' : 'bg-card_bg border border-border_soft text-text_secondary hover:text-text_primary hover:border-text_secondary'}`} title="Mark Absent"><XCircle size={16} strokeWidth={2.5}/></button>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-2 text-gold font-bold text-sm tracking-wide">
                        <Calculator size={14} className="opacity-70" />
                        ₹{calcPay.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setShowEdit(emp)} className="text-text_secondary hover:text-gold transition-colors bg-card_bg p-2 rounded-lg" title="Edit Profile"><Pencil size={16}/></button>
                        {emp.id !== "admin-super" && <button onClick={() => handleDelete(emp.id)} className="text-text_secondary hover:text-accent_danger transition-colors bg-card_bg p-2 rounded-lg" title="Delete Profile"><Trash2 size={16}/></button>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && <AddEmployeeModal onClose={() => setShowModal(false)} onAdd={handleAdd} branches={branches} />}
      {showEdit && <EditEmployeeModal emp={showEdit} onClose={() => setShowEdit(null)} onEdit={handleEdit} branches={branches} />}
    </div>
  );
}
